import AccountBoxOutlined from '@mui/icons-material/AccountBoxOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import MobileFriendlyOutlined from '@mui/icons-material/MobileFriendlyOutlined';
import PersonPinOutlined from '@mui/icons-material/PersonPinOutlined';
import SyncOutlined from '@mui/icons-material/SyncOutlined';
import { Button, Divider, Paper, Typography, useMediaQuery } from '@mui/material';
import { Provider } from 'graphql/generated/graphqlTypes';
import useChooseProvider from 'hooks/useChooseProvider/useChooseProvider';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import React, { useState } from 'react';
import Background from 'ui/atoms/Background/Background';
import Icon from 'ui/atoms/Icon/Icon';
import ComponentWrapper from '../ComponentWrapper/ComponentWrapper';

export interface Props {}

const ChoseProviderPage: React.FC<React.PropsWithChildren<Props>> = () => {
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });
  const { chooseProvider, loading } = useChooseProvider({
    handleFetchError
  });

  const onNextButtonClick = () => {
    if (!loading) chooseProvider(chosenProvider);
  };

  const [chosenProvider, setChosenProvider] = useState<Provider>(Provider.Cognito);

  const isExtraSmallScreen = useMediaQuery('(min-width: 0px) and (max-width:1122px)');
  const isSmallScreen = useMediaQuery('(min-width: 1123px) and (max-width:1154px)');
  const isMediumScreen = useMediaQuery('(min-width: 1155px) and (max-width:1234px)');

  const getPaperPadding = () => {
    if (isExtraSmallScreen || isSmallScreen) return '48px 24px';
    if (isMediumScreen) return '48px 40px';
    return '48px 80px';
  };

  const getPaperWidth = () => {
    if (isExtraSmallScreen) {
      return 'calc(100vw - 80px)';
    }
    if (isSmallScreen) {
      return '962px';
    }
    if (isMediumScreen) {
      return '994px';
    }
    return '1074px';
  };

  return (
    <ComponentWrapper width="100vw" height="100vh" alignItems="center" justifyContent="center">
      <Background id="background" />
      <Paper
        elevation={3}
        data-testid="login-window"
        id="login-window"
        style={{
          alignSelf: 'center',
          justifySelf: 'center',
          padding: getPaperPadding(),
          width: getPaperWidth(),
          borderRadius: '10px'
        }}
      >
        <ComponentWrapper flexDirection="column" gap="16px">
          <Typography variant="h4">Create ATLAS administrator account</Typography>
          <Typography variant="body1">
            Set up your admin access by creating an account or connecting an identity provider for seamless login.
          </Typography>
          <Divider />
          <ComponentWrapper flexDirection="row" id="content" gap="32px">
            <ComponentWrapper flexDirection="column" gap="16px" height="100%">
              <ComponentWrapper
                onClick={() => setChosenProvider(Provider.Cognito)}
                flexDirection="row"
                gap="8px"
                padding="0 16px"
                height="80px"
                alignItems="center"
                style={
                  chosenProvider === 'COGNITO'
                    ? {
                        borderRadius: '8px',
                        border: '1px solid #CC0',
                        backgroundColor: 'rgba(204, 204, 0, 0.08)',
                        cursor: 'pointer'
                      }
                    : {
                        borderRadius: '8px',
                        border: '1px solid #E0E0E0',
                        backgroundColor: '#FAFAFA',
                        cursor: 'pointer'
                      }
                }
              >
                <AccountBoxOutlined style={{ height: '36px', width: '36px', margin: '6px' }} />
                <Typography variant="body1">Create administrator account</Typography>
              </ComponentWrapper>
              <ComponentWrapper flexDirection="row" width="100%" justifyContent="center" alignItems="center" gap="16px">
                <Divider orientation="horizontal" style={{ height: '1px', width: '40%' }} />
                <Typography>or</Typography>
                <Divider orientation="horizontal" style={{ height: '1px', width: '40%' }} />
              </ComponentWrapper>
              <ComponentWrapper
                onClick={() => setChosenProvider(Provider.Entra)}
                flexDirection="row"
                gap="8px"
                padding="0 16px"
                height="80px"
                alignItems="center"
                style={
                  chosenProvider === 'ENTRA'
                    ? {
                        borderRadius: '8px',
                        border: '1px solid #CC0',
                        backgroundColor: 'rgba(204, 204, 0, 0.08)',
                        cursor: 'pointer'
                      }
                    : {
                        borderRadius: '8px',
                        border: '1px solid #E0E0E0',
                        backgroundColor: '#FAFAFA',
                        cursor: 'pointer'
                      }
                }
              >
                <Icon name="EntraId" height={36} width={36} />
                <Typography variant="body1">Connect Entra ID</Typography>
              </ComponentWrapper>
            </ComponentWrapper>
            <Divider orientation="vertical" />
            {chosenProvider === 'COGNITO' ? (
              <ComponentWrapper flexDirection="column" gap="16px">
                <ComponentWrapper gap="8px" alignItems="center">
                  <AccountBoxOutlined style={{ height: '36px', width: '36px', margin: '6px' }} />
                  <Typography variant="h5">Create administrator account</Typography>
                </ComponentWrapper>
                <ComponentWrapper alignItems="center" gap="8px" margin="8px">
                  <LockOutlined />
                  <ComponentWrapper flexDirection="column">
                    <Typography variant="body1">Email address &amp; password login</Typography>
                    <Typography variant="body2">
                      Administrator(s) can login with their email address and a personal password.
                    </Typography>
                  </ComponentWrapper>
                </ComponentWrapper>
              </ComponentWrapper>
            ) : (
              <ComponentWrapper flexDirection="column" gap="16px">
                <ComponentWrapper gap="8px" flexDirection="column">
                  <ComponentWrapper flexDirection="row" gap="14px">
                    <Icon name="EntraId" height={36} width={36} />
                    <Typography variant="h5">Microsoft Entra ID</Typography>
                  </ComponentWrapper>
                  <Typography variant="body2" fontStyle={'italic'}>
                    Connect EntraID to ATLAS, and benefit from the following features:
                  </Typography>
                </ComponentWrapper>
                <ComponentWrapper alignItems="center" gap="8px" margin="8px">
                  <LockOutlined />
                  <ComponentWrapper flexDirection="column">
                    <Typography variant="body1">Single Sign On</Typography>
                    <Typography variant="body2">Use your Microsoft credentials to log into ATLAS.</Typography>
                  </ComponentWrapper>
                </ComponentWrapper>
                <ComponentWrapper alignItems="center" gap="8px" margin="8px">
                  <SyncOutlined />
                  <ComponentWrapper flexDirection="column">
                    <Typography variant="body1">Automated user management</Typography>
                    <Typography variant="body2">Synchronize your Microsoft Entra ID user groups with ATLAS.</Typography>
                  </ComponentWrapper>
                </ComponentWrapper>
                <ComponentWrapper alignItems="center" gap="8px" margin="8px">
                  <MobileFriendlyOutlined />
                  <ComponentWrapper flexDirection="column">
                    <Typography variant="body1">Decentralized digital key management</Typography>
                    <Typography variant="body2">
                      Synchronized users can manage their own mobile device in the ATLAS Portal.
                    </Typography>
                  </ComponentWrapper>
                </ComponentWrapper>
                <ComponentWrapper alignItems="center" gap="8px" margin="8px">
                  <PersonPinOutlined />
                  <ComponentWrapper flexDirection="column">
                    <Typography variant="body1">Improved visitor management</Typography>
                    <Typography variant="body2">
                      Seamlessly host visitors with integrated Outlook scheduling and mobile access.
                    </Typography>
                  </ComponentWrapper>
                </ComponentWrapper>
              </ComponentWrapper>
            )}
          </ComponentWrapper>
          <Divider />
          <ComponentWrapper alignSelf="flex-end" width="106px">
            <Button onClick={onNextButtonClick} variant="contained" size="large" color="primary" fullWidth>
              Continue
            </Button>
          </ComponentWrapper>
        </ComponentWrapper>
      </Paper>
    </ComponentWrapper>
  );
};

export default ChoseProviderPage;
